import { SafetyMetadataParsedContent } from './general.model';

/**
 * Parsed safety custom header data
 */
export interface SafetyCustomHeaderData {
  countryCode: number;
  regionCode: number;
  version: number;
}

/**
 * Regex pattern for safety custom header
 * Format: COUNTRY&Region&Ver:{countryCode},{regionCode},{version}
 */
const SAFETY_HEADER_REGEX = /COUNTRY&Region&Ver:(\d+),(\d+),(\d+)/;

/**
 * Parse safety custom file header to extract country/region/version
 * @param content - The content string (first line or full content)
 * @returns Parsed data or null if header is invalid
 */
export const parseSafetyCustomHeader = (content: string): SafetyCustomHeaderData | null => {
  const firstLine = content.split('\n')[0];
  const match = firstLine.match(SAFETY_HEADER_REGEX);

  if (!match) {
    return null;
  }

  return {
    countryCode: Number(match[1]),
    regionCode: Number(match[2]),
    version: Number(match[3])
  };
};

/**
 * Build override key from safety header data
 * Format: {countryCode}_{regionCode}_{version}
 */
export const buildSafetyOverrideKey = (data: SafetyCustomHeaderData): string => {
  return `${data.countryCode}_${data.regionCode}_${data.version}`;
};

/**
 * Split an array into subarray based on a sequence.
 */
export const splitUint8Array = (array: Uint8Array, sequence: Uint8Array): Uint8Array[]  => {
  const arrayCopy = Array.from(array);
  const sequenceCopy = Array.from(sequence);
  const result: Uint8Array[] = [];
  let startIndex = 0;

  while (startIndex < array.length) {
    const sequenceIndex = arrayCopy.findIndex(
      (value, index) => {
        if (index < startIndex) {
          return false;
        }
        for (let i = 0; i < sequenceCopy.length; i++) {
          if (sequenceCopy[i] !== arrayCopy[index + i]) {
            return false;
          }
        }
        return true;
      },
    );

    if (sequenceIndex === -1) {
      result.push(array.slice(startIndex));
      break;
    }

    result.push(array.slice(startIndex, sequenceIndex));
    startIndex = sequenceIndex + sequence.length;
  }

  return result;
};

export const parseData = (input: string) => {
  const data: SafetyMetadataParsedContent = {};

  // Parsing COUNTRY, Region e Ver
  const [header, ...blocks] = input.split('\n');
  const [country, region, ver] = header.split(':').pop()?.split(',') ?? [];
  data.country = Number(country);
  data.region = Number(region);
  data.ver = Number(ver);

  data.blocks = {};
  // Parsing of blocks
  for (const block of blocks) {
    const [key, value] = block.split(':');
    data.blocks[key] = value.split(',')
      .map((v) => v.trim())
      .filter((v) => v !== '')
      .map((v) => Number(v));
  }

  return data;
};
